import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { cn } from '@/lib/utils';

/**
 * Async searchable dropdown: fetches options from API when user opens or types.
 * fetchOptions(query) => Promise<Array<{ value, label, country? }>>
 * onChange(value, item?) so parent can store code + country
 * selectedLabel: display when value is set (e.g. from initialData; parent can fetch by code)
 */
export function AsyncSearchableSelect({
  fetchOptions,
  value = '',
  onChange,
  selectedLabel = '',
  placeholder = 'Select...',
  disabled = false,
  className = '',
  error = false,
  debounceMs = 300,
  limit = 30,
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);
  const contentRef = useRef(null);

  const loadOptions = useCallback(async (q) => {
    if (!fetchOptions) return;
    setLoading(true);
    try {
      const result = await fetchOptions(q, limit);
      setOptions(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('AsyncSearchableSelect fetch failed:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchOptions, limit]);

  // When dropdown opens: load initial options; when user types: debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const isInitial = searchQuery === '';
    const delay = isInitial ? 0 : debounceMs;
    debounceRef.current = setTimeout(() => {
      loadOptions(searchQuery);
      debounceRef.current = null;
    }, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, open, debounceMs, loadOptions]);

  const displayLabel = selectedLabel || (options.find((o) => o.value === value)?.label) || (value && value !== '' ? value : '');

  const handleSelect = (opt) => {
    onChange(opt.value, opt);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) setSearchQuery('');
    setOpen(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors',
            'focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-400',
            'data-[state=open]:border-gray-400 data-[state=open]:ring-1 data-[state=open]:ring-gray-800',
            'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            error && 'border-red-500',
            className
          )}
        >
          <span className={!displayLabel ? 'text-muted-foreground' : ''}>
            {displayLabel || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[var(--radix-popover-trigger-width)] min-w-[16rem] max-w-[24rem] p-0 rounded-lg shadow-lg border border-border bg-popover"
        align="start"
        side="bottom"
        avoidCollisions={false}
      >
        <Command shouldFilter={false} className="rounded-lg overflow-hidden max-h-[min(22rem,calc(100vh-8rem))]">
          <CommandInput
            placeholder="Type to search..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && (
              <>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => handleSelect(opt)}
                    >
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
